"use client";

import { useState, useCallback } from "react";
import { type User } from "firebase/auth";
import { apiFetch } from "@/lib/api/client";
import type { Novel } from "@/lib/types/novel";
import type { GachaResult } from "@/lib/types/api";
import type { UserData } from "@/lib/types/user";

/** 配布の様子が届いていないときの1冊目の値段（src/lib/magic-supply.ts の既定の表の1冊目） */
export const FALLBACK_GACHA_COST = 10;

interface GachaState {
  phase: "idle" | "rolling" | "result" | "error";
  result: Novel | null;
  error: string | null;
  errorType: "retryable" | "exhausted" | null;
}

/** 引いた後の残りの魔力と次の値段（ページを開き直すまでは userData が古いので、ガチャの返事で持ち直す） */
interface Wallet {
  points: number;
  nextDrawPrice: number;
}

export function useGacha(user: User | null | undefined, userData?: UserData | null) {
  const [state, setState] = useState<GachaState>({
    phase: "idle",
    result: null,
    error: null,
    errorType: null,
  });
  const [wallet, setWallet] = useState<Wallet | null>(null);

  const roll = useCallback(async () => {
    if (!user) return;

    setState({ phase: "rolling", result: null, error: null, errorType: null });

    try {
      const token = await user.getIdToken();
      const { novel, newPoints, nextDrawPrice } = await apiFetch<GachaResult>("/api/gacha", token, {
        method: "POST",
      });
      setWallet({ points: newPoints.free + newPoints.paid, nextDrawPrice });

      // 待ち時間は置かない。演出（ガチャ画面の BookDrawRitual）は返事が届くまで本棚を出して待ち、届いてから本を光らせる
      setState({ phase: "result", result: novel, error: null, errorType: null });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("NO_NOVELS_LEFT")) {
        setState({
          phase: "error",
          result: null,
          error: "未読の小説がもうありません",
          errorType: "exhausted",
        });
      } else if (msg.includes("INSUFFICIENT_POINTS")) {
        setState({
          phase: "error",
          result: null,
          error: "魔力が不足しています",
          errorType: "retryable",
        });
      } else {
        console.error("Gacha error:", err);
        setState({
          phase: "error",
          result: null,
          error: "ガチャの実行中にエラーが発生しました",
          errorType: "retryable",
        });
      }
    }
  }, [user]);

  const reset = useCallback(() => {
    setState({ phase: "idle", result: null, error: null, errorType: null });
  }, []);

  const totalPoints = wallet?.points ?? (userData ? userData.magicPoints.free + userData.magicPoints.paid : 0);
  const gachaCost = wallet?.nextDrawPrice ?? userData?.magicSupply?.nextDrawPrice ?? FALLBACK_GACHA_COST;

  return {
    phase: state.phase,
    result: state.result,
    error: state.error,
    errorType: state.errorType,
    roll,
    reset,
    gachaCost,
    totalPoints,
  };
}
